/**
 * Check in (1.0.0-rc.7): bring an edited NLE timeline back. The dialog opens on a hand-off ("Check in…" in the Sent
 * to edit list, or the editor's Sent to edit banner) or as "Import NLE sequence" (a hand-off, or a file from this
 * computer, matching only). It lists the hand-off folder, reads the chosen timeline, shows the dry-run report and
 * applies it as a new version of the sequence (with this tab's edit lock) or as a new sequence.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, FileInput, HelpCircle, Loader2, Upload } from 'lucide-react';
import {
  PROGRAMME_VARIANT_KEY, formatDuration, frameRateLabel, type CheckinDTO, type CheckinReportDTO, type HandoffDTO, type HandoffFolderDTO, type MasterConflict,
} from '@prism/contracts';
import { api, errorMessage } from '../api/client';
import { useHandoffs, useMasterVariants } from '../api/hooks';
import { queryKeys } from '../api/queryKeys';
import { bytes, relative } from '../lib/format';
import { Modal } from './Modal';
import { toast } from './Toasts';
import { TableScroll } from './TableScroll';

const STEP: Record<string, string> = {
  fetching: 'Fetching the timeline from the site', converting: 'Reading the AAF', matching: 'Matching the clips',
  ingesting: 'Bringing in the new media', applying: 'Applying the edit',
};
const VIA: Record<string, string> = { manifest: 'Hand-off manifest', checkin: 'An earlier check-in', filename: 'File name', reel_timecode: 'Reel and timecode' };

/** A check-in, refreshed while it is being read or applied. */
function useCheckin(id: string | null) {
  return useQuery({
    queryKey: ['checkin', id],
    queryFn: () => api<CheckinDTO>(`/api/v1/checkins/${id}`),
    enabled: Boolean(id),
    refetchInterval: (q) => (q.state.data && (q.state.data.status === 'reading' || q.state.data.status === 'applying') ? 1000 : false),
  });
}

/** The dry-run report: what matched, what comes in, placeholders, what is left out, and the differences. */
export function CheckinReport({ report }: { report: CheckinReportDTO }) {
  const [show, setShow] = useState<'matched' | 'changes' | null>(null);
  const t = report.timeline;
  const counts: Array<[string, number, string]> = [
    ['Matched', report.matched.length, 'checkin-matched'], ['Graphics', report.graphics.length, 'checkin-graphics'],
    [`New media${report.classes?.media ? ` → ${report.classes.media.name}` : ''}`, report.ingest.length, 'checkin-ingest'], ['Placeholders', report.placeholders.length, 'checkin-placeholders'],
    ['Left out', report.dropped.length, 'checkin-dropped'],
  ];
  const d = report.differences;
  return (
    <div data-testid="checkin-report" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p className="muted" style={{ margin: 0 }}>
        <strong>{t.name}</strong> from {t.app} · {frameRateLabel(t.frameRate)} fps{t.width ? ` · ${t.width}×${t.height}` : ''} · {t.videoTracks} video and {t.audioTracks} sound tracks, {t.clips} clips · {formatDuration(t.durationTicks)}
      </p>
      {report.editedSinceHandoff && (
        <div className="banner warning" data-testid="checkin-edited-since"><AlertTriangle size={14} /><div className="banner-body">
          <strong>The sequence changed in PRISM after it was sent</strong>
          <span>{report.editedSinceHandoff.by ?? 'Someone'} saved revision {report.editedSinceHandoff.currentRevision} {relative(report.editedSinceHandoff.at)} (it was sent at revision {report.editedSinceHandoff.sentRevision}). A new version replaces those changes; the version before is kept under Versions, or check in as a new sequence.</span>
        </div></div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 8 }}>
        {counts.map(([label, n, id]) => (
          <div key={label} className="panel-card" style={{ padding: '8px 10px' }} data-testid={id}>
            <div className="faint" style={{ fontSize: 11 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{n}</div>
          </div>
        ))}
      </div>
      <p className="muted" style={{ margin: 0, fontSize: 12.5 }} data-testid="checkin-differences">
        {d.before ? `${d.before.clips} clips, ${formatDuration(d.before.durationTicks)} when sent → ` : ''}{d.after.clips} clips, {formatDuration(d.after.durationTicks)}
        {d.changes.length ? ` · ${(['trimmed', 'moved', 'added', 'removed'] as const).map((k) => [k, d.changes.filter((c) => c.change === k).length] as const).filter(([, n]) => n).map(([k, n]) => `${n} ${k}`).join(', ')}` : d.before ? ' · the same cut' : ''}
        {d.changes.length > 0 && <> <button className="btn ghost sm" onClick={() => setShow(show === 'changes' ? null : 'changes')}>{show === 'changes' ? 'Hide' : 'Show'} changes</button></>}
        {report.matched.length > 0 && <> <button className="btn ghost sm" onClick={() => setShow(show === 'matched' ? null : 'matched')} data-testid="checkin-show-matched">{show === 'matched' ? 'Hide' : 'Show'} matched clips</button></>}
      </p>
      {show === 'changes' && (
        <TableScroll><table className="table compact"><tbody>{d.changes.map((c, i) => <tr key={i}><td className="wrap">{c.label}</td><td className="muted wrap">{c.change}</td></tr>)}</tbody></table></TableScroll>
      )}
      {show === 'matched' && (
        <TableScroll><table className="table compact" data-testid="checkin-matched-list"><thead><tr><th>Clip</th><th>Track</th><th>PRISM asset</th><th>Matched by</th></tr></thead>
          <tbody>{report.matched.map((m, i) => <tr key={i}><td className="wrap">{m.name}</td><td className="mono nowrap">{m.track}</td><td className="wrap">{m.title}</td><td className="muted wrap">{VIA[m.via]}</td></tr>)}</tbody></table></TableScroll>
      )}
      {report.ingest.length > 0 && (
        <div><p className="section-label" style={{ margin: '4px 0' }}>New media, ingested on Apply</p>
          {report.classes?.media && <p className="muted" style={{ margin: '0 0 4px', fontSize: 12.5 }} data-testid="checkin-ingest-class">{report.ingest.length} new clip{report.ingest.length === 1 ? '' : 's'} → {report.classes.media.name}</p>}
          <ul style={{ margin: 0, paddingLeft: 18 }} data-testid="checkin-ingest-list">{report.ingest.map((i) => <li key={i.path}><span className="mono">{i.shared ? i.path.slice(1) : i.path}</span> <span className="faint">· {i.clips} clip{i.clips === 1 ? '' : 's'}{i.shared ? ' · elsewhere on the edit location' : ''}</span></li>)}</ul></div>
      )}
      {report.placeholders.length > 0 && (
        <div data-testid="checkin-placeholder-list"><p className="section-label" style={{ margin: '4px 0' }}>Not in PRISM: placeholders{report.classes?.media ? ` (${report.classes.media.name})` : ''}, and a gap on V1 where they were</p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>{report.placeholders.map((p, i) => <li key={i}>{p.name} <span className="faint">· {formatDuration(p.durationTicks)}{p.file ? ` · ${p.file}` : ''}{p.outside ? ' · outside the edit location' : ''}</span></li>)}</ul></div>
      )}
      {report.graphics.some((g) => g.moved) && <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>{report.graphics.filter((g) => g.moved).length} PRISM graphic{report.graphics.filter((g) => g.moved).length === 1 ? ' was' : 's were'} moved or retimed in the NLE; their text and settings stay as they are in PRISM.</p>}
      {(report.dropped.length > 0 || report.notes.length > 0) && (
        <div className="banner info" data-testid="checkin-notes"><div className="banner-body">
          {report.dropped.slice(0, 20).map((x, i) => <span key={`d${i}`}><strong>{x.name}</strong> ({x.track}): {x.reason}</span>)}
          {report.dropped.length > 20 && <span>and {report.dropped.length - 20} more left out</span>}
          {report.notes.map((n, i) => <span key={`n${i}`}>{n}</span>)}
        </div></div>
      )}
      {report.master && <p className="muted" style={{ margin: 0, fontSize: 12.5 }} data-testid="checkin-master-note">The master <span className="mono">{report.master.name}</span> becomes an asset{report.classes?.master ? ` (${report.classes.master.name})` : ''} that uses everything in the edit.</p>}
    </div>
  );
}

/** Apply: a new version of the sequence that was sent, or a new sequence. */
function ApplyChoice({ c, tabId, onApplied }: { c: CheckinDTO; tabId?: string; onApplied: (c: CheckinDTO) => void }) {
  const r = c.report!;
  const [as, setAs] = useState<'version' | 'sequence' | 'master'>(r.canApplyAsVersion ? 'version' : 'sequence');
  const [name, setName] = useState(`${c.sequenceName ?? c.name} (from ${r.timeline.app})`);
  // 1.2.0: the rendered master can instead become a master of the sequence's asset, with a variant.
  const variants = useMasterVariants(Boolean(r.master)).data?.items ?? [];
  const [variantId, setVariantId] = useState<string>('');
  const [onConflict, setOnConflict] = useState<MasterConflict>('refuse');
  const variant = variants.find((v) => v.id === variantId) ?? variants.find((v) => v.key === PROGRAMME_VARIANT_KEY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function apply() {
    setBusy(true);
    setError(null);
    try {
      const extra = as === 'sequence' ? { name: name.trim() || null }
        : as === 'master' ? { variantId: variant && variant.key !== PROGRAMME_VARIANT_KEY ? variant.id : null, onConflict }
        : {};
      const res = await api<CheckinDTO>(`/api/v1/checkins/${c.id}/apply`, { method: 'POST', headers: tabId ? { 'x-prism-tab': tabId } : {}, json: { as, ...extra } });
      onApplied(res);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }} data-testid="checkin-apply">
      {error && <div className="banner error">{error}</div>}
      <label className="toggle-row"><input type="radio" name="checkin-as" checked={as === 'version'} disabled={!r.canApplyAsVersion} onChange={() => setAs('version')} data-testid="checkin-as-version" />
        <span><strong>A new version of “{c.sequenceName ?? c.name}”</strong> <span className="faint">· the version before is kept under Versions; needs the edit lock</span>{r.versionProblem && <span className="help" style={{ display: 'block' }}>{r.versionProblem}</span>}</span></label>
      <label className="toggle-row"><input type="radio" name="checkin-as" checked={as === 'sequence'} onChange={() => setAs('sequence')} data-testid="checkin-as-sequence" />
        <span><strong>A new sequence</strong></span></label>
      {as === 'sequence' && <input className="input" value={name} onChange={(e) => setName(e.target.value)} aria-label="Name of the new sequence" data-testid="checkin-name" style={{ maxWidth: 420 }} />}
      {r.master && (
        <label className="toggle-row"><input type="radio" name="checkin-as" checked={as === 'master'} onChange={() => setAs('master')} data-testid="checkin-as-master" />
          <span><strong>Only the rendered master, as a master of the sequence’s asset</strong> <span className="faint">· <span className="mono">{r.master.name}</span>; the hand-off’s only source asset, else the asset an earlier check-in of this sequence made; it must be the same content as that asset’s primary master</span></span></label>
      )}
      {as === 'master' && (
        <div className="field-row" style={{ maxWidth: 620 }}>
          <div className="field"><label htmlFor="checkin-variant">Variant</label>
            <select id="checkin-variant" className="select" value={variant?.id ?? ''} onChange={(e) => setVariantId(e.target.value)} data-testid="checkin-variant">
              {variants.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.code})</option>)}
            </select></div>
          <div className="field"><label htmlFor="checkin-conflict">If it has that variant in the same format</label>
            <select id="checkin-conflict" className="select" value={onConflict} onChange={(e) => setOnConflict(e.target.value as MasterConflict)} data-testid="checkin-conflict">
              <option value="refuse">Stop and tell me</option>
              <option value="replace">Replace it (kept until the new one is verified)</option>
              <option value="skip">Keep the existing one</option>
            </select></div>
        </div>
      )}
      <div><button className="btn primary" onClick={() => void apply()} disabled={busy} data-testid="checkin-apply-button">{busy ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Apply</button></div>
    </div>
  );
}

/** Reading, the report and Apply for one check-in. */
function CheckinProgress({ id, tabId, onDone }: { id: string; tabId?: string; onDone: (c: CheckinDTO) => void }) {
  const q = useCheckin(id);
  const qc = useQueryClient();
  const c = q.data;
  useEffect(() => { if (c?.status === 'applied') onDone(c); }, [c?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!c) return <p className="muted"><Loader2 size={14} className="spin" /> Reading…</p>;
  if (c.status === 'failed') return <div className="banner error" data-testid="checkin-failed"><div className="banner-body"><strong>{c.error?.message ?? 'The check-in failed'}</strong>{c.error?.hint && <span>{c.error.hint}</span>}</div></div>;
  if (c.status === 'reading' || c.status === 'applying') {
    return <p className="muted" data-testid="checkin-busy"><Loader2 size={14} className="spin" /> {STEP[c.step ?? ''] ?? (c.status === 'reading' ? 'Reading the timeline' : 'Applying')}…</p>;
  }
  if (c.status === 'applied' && c.result) {
    return (
      <div className="banner info" data-testid="checkin-applied"><Check size={14} /><div className="banner-body">
        <strong>Checked in {c.result.asNewSequence ? `as “${c.result.sequenceName}”` : `as revision ${c.result.revision} of “${c.result.sequenceName}”`}</strong>
        {c.result.fallback && <span>{c.result.fallback}</span>}
        <span>{c.result.ingested.length} new media, {c.result.placeholders.length} placeholder{c.result.placeholders.length === 1 ? '' : 's'}{c.result.masterAssetId ? ', the master as an asset' : ''}.</span>
        {(c.result.alreadyInPrism?.length ?? 0) > 0 && (
          <span data-testid="checkin-already-in-prism">Already in PRISM, so not brought in again: {c.result.alreadyInPrism!.map((a, i) => <span key={a.path}>{i ? ', ' : ''}<Link to={`/assets/${a.assetId}`}>{a.title}</Link></span>)}.</span>
        )}
        <span><Link to={`/create/${c.result.sequenceId}`}>Open the sequence</Link>{c.result.masterAssetId && <> · <Link to={`/assets/${c.result.masterAssetId}`}>Open the master</Link></>}</span>
      </div></div>
    );
  }
  return (
    <>
      <CheckinReport report={c.report!} />
      <ApplyChoice c={c} tabId={tabId} onApplied={(next) => { qc.setQueryData(['checkin', id], next); if (next.status === 'applied') onDone(next); }} />
    </>
  );
}

/** Choosing what to check in from a hand-off folder: the timeline, new media and the master. */
function FolderPicker({ handoff, onStarted }: { handoff: HandoffDTO; onStarted: (c: CheckinDTO) => void }) {
  const q = useQuery({ queryKey: ['handoff-folder', handoff.id], queryFn: () => api<HandoffFolderDTO>(`/api/v1/handoffs/${handoff.id}/folder`), retry: false, staleTime: 0 });
  const entries = q.data?.entries ?? [];
  const timelines = entries.filter((e) => e.kind === 'timeline');
  const mediaFiles = entries.filter((e) => e.kind === 'media' && !e.fromPrism);
  const [timeline, setTimeline] = useState('');
  const [media, setMedia] = useState<string[] | null>(null);
  const [master, setMaster] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chosenTimeline = timeline || timelines.find((t) => !t.fromPrism)?.path || '';
  // Files already in PRISM are never offered (fixes after rc.9 testing): listed, not ticked, with a link to the asset.
  const chosenMedia = media ?? mediaFiles.filter((m) => m.path.startsWith('_to_prism/') && m.path !== master && !m.inPrism).map((m) => m.path);
  async function start() {
    setBusy(true);
    setError(null);
    try {
      const c = await api<CheckinDTO>(`/api/v1/handoffs/${handoff.id}/checkins`, { method: 'POST', json: { timeline: chosenTimeline, media: chosenMedia.filter((m) => m !== master), master: master || null } });
      onStarted(c);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  if (q.isLoading) return <p className="muted"><Loader2 size={14} className="spin" /> Listing the hand-off folder…</p>;
  if (q.error) return <div className="banner error">{errorMessage(q.error)}</div>;
  return (
    <div data-testid="checkin-folder" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {error && <div className="banner error">{error}</div>}
      <p className="muted" style={{ margin: 0 }}>Export the edited timeline from the NLE into <span className="mono">{q.data?.workstationFolder ?? handoff.folder}/_to_prism/</span>, with any new media and a rendered master, then choose them here.</p>
      <div className="field"><label>Timeline</label>
        {timelines.length === 0 ? <p className="faint" style={{ margin: 0 }} data-testid="checkin-no-timeline">No timeline in the folder yet (.xml, .fcpxml, .otio, .aaf or .edl).</p> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 180, overflow: 'auto' }}>
            {timelines.map((t) => (
              <label key={t.path} className="toggle-row"><input type="radio" name="checkin-timeline" checked={chosenTimeline === t.path} onChange={() => setTimeline(t.path)} data-testid="checkin-timeline-option" />
                <span><span className="mono">{t.path}</span> <span className="faint">· {t.format?.toUpperCase()}{t.sizeBytes != null ? ` · ${bytes(t.sizeBytes)}` : ''}{t.modifiedAt ? ` · ${relative(t.modifiedAt)}` : ''}{t.fromPrism ? ' · as sent by PRISM' : ''}</span></span></label>
            ))}
          </div>
        )}
      </div>
      {mediaFiles.length > 0 && (
        <div className="field"><label>New media to bring into PRISM</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 160, overflow: 'auto' }}>
            {mediaFiles.filter((m) => m.path !== master).map((m) => (
              <label key={m.path} className="toggle-row" data-in-prism={m.inPrism ? 'true' : undefined}>
                <input type="checkbox" checked={!m.inPrism && chosenMedia.includes(m.path)} disabled={Boolean(m.inPrism)} onChange={(e) => setMedia(e.target.checked ? [...chosenMedia, m.path] : chosenMedia.filter((x) => x !== m.path))} data-testid="checkin-media-option" />
                <span><span className="mono">{m.path}</span> <span className="faint">· {bytes(m.sizeBytes)}</span>
                  {m.inPrism && <span className="faint" data-testid="checkin-media-in-prism"> · Already in PRISM: <Link to={`/assets/${m.inPrism.assetId}`}>{m.inPrism.title}</Link></span>}</span></label>
            ))}
          </div>
          <span className="help">Only files the timeline uses are ingested; each becomes an asset. Files it uses from elsewhere on the edit location (a shared Music or Stock folder) are offered in the report without choosing them here.</span>
        </div>
      )}
      {mediaFiles.length > 0 && (
        <div className="field"><label htmlFor="checkin-master">Rendered master (optional)</label>
          <select id="checkin-master" className="select" value={master} onChange={(e) => setMaster(e.target.value)} data-testid="checkin-master">
            <option value="">None</option>
            {mediaFiles.filter((m) => !m.inPrism).map((m) => <option key={m.path} value={m.path}>{m.path}</option>)}
          </select>
          <span className="help">It becomes an asset that uses everything in the edit.</span>
        </div>
      )}
      {q.data?.truncated && <p className="help" style={{ margin: 0 }}>The folder has more files than are listed.</p>}
      <div><button className="btn primary" disabled={busy || !chosenTimeline} onClick={() => void start()} data-testid="checkin-read">{busy ? <Loader2 size={14} className="spin" /> : <FileInput size={14} />} Read the timeline</button></div>
    </div>
  );
}

/** Check in… on a hand-off. `tabId`: the editor tab's id, so a new version can be applied with its edit lock. */
export function CheckInDialog({ handoff, tabId, onClose, onApplied }: { handoff: HandoffDTO; tabId?: string; onClose: () => void; onApplied?: (c: CheckinDTO) => void }) {
  const qc = useQueryClient();
  const [checkinId, setCheckinId] = useState<string | null>(null);
  const done = (c: CheckinDTO) => {
    void qc.invalidateQueries({ queryKey: queryKeys.handoffs() });
    void qc.invalidateQueries({ queryKey: queryKeys.sequences() });
    onApplied?.(c);
  };
  return (
    <Modal title={`Check in: ${handoff.name}`} wide onClose={onClose} actions={<>
      <Link to="/help/nle#checkin" className="btn ghost sm" style={{ marginRight: 'auto' }} data-testid="checkin-help"><HelpCircle size={13} /> Help</Link>
      {checkinId && <button className="btn ghost" onClick={() => setCheckinId(null)} data-testid="checkin-another">Choose another file</button>}
      <button className="btn" onClick={onClose}>Close</button>
    </>}>
      <div data-testid="checkin-dialog">
        {checkinId ? <CheckinProgress id={checkinId} tabId={tabId} onDone={done} /> : <FolderPicker handoff={handoff} onStarted={(c) => setCheckinId(c.id)} />}
      </div>
    </Modal>
  );
}

/** Hidden from Import NLE sequence by default (fixes after rc.9 testing): its folder is gone, cleaned up, or removed from the Import list. */
export const handoffHidden = (h: HandoffDTO) => Boolean(h.cleanedAt || h.removedAt || h.folderCheck?.missing);

/** Open hand-offs (not checked in) first, newest first, then the checked-in ones, newest first. */
const pickerOrder = (a: HandoffDTO, b: HandoffDTO) => Number(Boolean(a.checkedInAt)) - Number(Boolean(b.checkedInAt)) || (b.finishedAt ?? b.createdAt).localeCompare(a.finishedAt ?? a.createdAt);

/** How a hand-off's record stands: checked in, its folder missing or not checkable now, cleaned up, removed. */
export function HandoffState({ h }: { h: HandoffDTO }) {
  const parts: Array<[string, string, boolean]> = [];
  if (h.checkedInAt) parts.push(['checked-in', `Checked in ${relative(h.checkedInAt)}`, false]);
  if (h.removedAt) parts.push(['removed', `Removed from the Import list ${relative(h.removedAt)}`, false]);
  if (h.cleanedAt) parts.push(['cleaned', `Folder cleaned up ${relative(h.cleanedAt)}`, false]);
  else if (h.folderCheck?.missing) parts.push(['missing', `Folder missing since ${relative(h.folderCheck.missingSince)}`, true]);
  else if (h.folderCheck?.unreachable) parts.push(['unreachable', `Can’t check now: ${h.folderCheck.unreachable}`, true]);
  if (!parts.length) return null;
  return <>{parts.map(([k, text, warn]) => <span key={k} className="faint" style={warn ? { color: 'var(--warning)' } : undefined} data-testid={`handoff-state-${k}`}> · {text}</span>)}</>;
}

/** What Remove from the Import list does, for its button's title. */
export const REMOVE_HANDOFF_TITLE = 'Hide this hand-off in Import NLE sequence. Its folder and files stay, and the sequence’s Exports list still shows it (Show in the Import list again undoes it).';

/**
 * Remove from the Import list (fixes after rc.9 testing): hides the hand-off in Import NLE sequence only; nothing in its
 * folder is touched and the sequence's Exports list keeps it. Show in the Import list again (restoreHandoff) undoes it.
 */
export async function removeHandoff(h: HandoffDTO, qc: ReturnType<typeof useQueryClient>) {
  try {
    await api(`/api/v1/handoffs/${h.id}/remove`, { method: 'POST', json: {} });
    toast(`“${h.name}” removed from the Import list; its folder, files and the Exports history stay`);
    await qc.invalidateQueries({ queryKey: queryKeys.handoffs() });
  } catch (err) {
    toast(errorMessage(err), 'error');
  }
}

/** Show in the Import list again: undoes Remove from the Import list. */
export async function restoreHandoff(h: HandoffDTO, qc: ReturnType<typeof useQueryClient>) {
  try {
    await api(`/api/v1/handoffs/${h.id}/restore`, { method: 'POST', json: {} });
    toast(`“${h.name}” is in the Import list again`);
    await qc.invalidateQueries({ queryKey: queryKeys.handoffs() });
  } catch (err) {
    toast(errorMessage(err), 'error');
  }
}

const EDL_RATES = ['25', '24', '24000/1001', '30000/1001', '50', '60000/1001', '30', '60'];

/** Import NLE sequence: from a hand-off (Check in), or a timeline file from this computer (matching only, a new sequence). */
export function ImportNleDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [source, setSource] = useState<'file' | 'handoff'>('file');
  // Fixes after rc.9 testing: open hand-offs first, newest first, then checked-in ones; missing, cleaned-up and removed
  // ones only with the toggle. Their folders are checked when the list opens.
  const [showAll, setShowAll] = useState(false);
  const listed = useHandoffs(undefined, { includeRemoved: showAll });
  useEffect(() => {
    if (source !== 'handoff') return;
    void api('/api/v1/handoffs/check-folders', { method: 'POST', json: {} }).then(() => qc.invalidateQueries({ queryKey: queryKeys.handoffs() })).catch(() => undefined);
  }, [source]); // eslint-disable-line react-hooks/exhaustive-deps
  const ready = (listed.data?.items ?? []).filter((h) => h.status === 'ready');
  const hiddenCount = ready.filter(handoffHidden).length;
  const handoffs = ready.filter((h) => showAll || !handoffHidden(h)).sort(pickerOrder);
  const [handoff, setHandoff] = useState<HandoffDTO | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fps, setFps] = useState('25');
  const [checkinId, setCheckinId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEdl = useMemo(() => Boolean(file?.name.toLowerCase().endsWith('.edl')), [file]);
  async function upload() {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const c = await api<CheckinDTO>(`/api/v1/checkins/upload?name=${encodeURIComponent(file.name)}${isEdl ? `&fps=${encodeURIComponent(fps)}` : ''}`, { method: 'POST', headers: { 'content-type': 'application/octet-stream' }, body: file });
      setCheckinId(c.id);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  if (handoff) return <CheckInDialog handoff={handoff} onClose={onClose} onApplied={(c) => { if (c.result) navigate(`/create/${c.result.sequenceId}`); }} />;
  return (
    <Modal title="Import NLE sequence" wide onClose={onClose} actions={<>
      <Link to="/help/nle#checkin" className="btn ghost sm" style={{ marginRight: 'auto' }} data-testid="import-nle-help"><HelpCircle size={13} /> Help</Link>
      <button className="btn" onClick={onClose}>Close</button>
    </>}>
      <div data-testid="import-nle">
        {error && <div className="banner error">{error}</div>}
        {checkinId ? (
          <CheckinProgress id={checkinId} onDone={(c) => { void qc.invalidateQueries({ queryKey: queryKeys.sequences() }); toast(`Imported as “${c.result?.sequenceName}”`); }} />
        ) : (
          <>
            <div className="segmented" role="radiogroup" aria-label="From" style={{ marginBottom: 10 }}>
              <button aria-pressed={source === 'file'} onClick={() => setSource('file')} data-testid="import-from-file">A file from this computer</button>
              <button aria-pressed={source === 'handoff'} onClick={() => setSource('handoff')} data-testid="import-from-handoff">A hand-off folder</button>
            </div>
            {source === 'file' ? (
              <>
                <p className="muted" style={{ marginTop: 0 }}>A timeline from any NLE: FCP7 XML (.xml), FCPXML (.fcpxml), OTIO (.otio), AAF (.aaf) or a CMX 3600 EDL (.edl). PRISM matches its clips to the library by file name, then by reel and timecode, and makes a new sequence; clips it cannot find become placeholders.</p>
                <div className="field"><label htmlFor="import-file">Timeline file</label>
                  <input id="import-file" type="file" accept=".xml,.fcpxml,.otio,.aaf,.edl" onChange={(e) => setFile(e.target.files?.[0] ?? null)} data-testid="import-file" /></div>
                {isEdl && (
                  <div className="field"><label htmlFor="import-fps">Frame rate</label>
                    <select id="import-fps" className="select" value={fps} onChange={(e) => setFps(e.target.value)} style={{ maxWidth: 200 }} data-testid="import-fps">
                      {EDL_RATES.map((r) => <option key={r} value={r}>{frameRateLabel({ num: Number(r.split('/')[0]), den: Number(r.split('/')[1] ?? 1) })} fps</option>)}
                    </select><span className="help">An EDL does not say its frame rate.</span></div>
                )}
                <button className="btn primary" disabled={!file || busy} onClick={() => void upload()} data-testid="import-read">{busy ? <Loader2 size={14} className="spin" /> : <Upload size={14} />} Read the timeline</button>
              </>
            ) : (
              <>
                {handoffs.length === 0 ? (
                  <p className="muted" data-testid="import-no-handoffs">No hand-offs ready to check in.</p>
                ) : (
                  <TableScroll><table className="table compact" data-testid="import-handoffs"><tbody>{handoffs.map((h) => {
                    const closed = handoffHidden(h);
                    return (
                      <tr key={h.id} data-testid="import-handoff-row" data-handoff={h.id}>
                        <td className="wrap" style={{ minWidth: 140 }}>{h.name}</td>
                        <td className="muted wrap">{h.location.name} · {relative(h.finishedAt ?? h.createdAt)}<HandoffState h={h} /></td>
                        <td className="actions">
                          {h.canRemove && <button className="btn ghost sm" onClick={() => void removeHandoff(h, qc)} title={REMOVE_HANDOFF_TITLE} data-testid="remove-handoff">Remove from the Import list</button>}
                          {h.canRestore && <button className="btn ghost sm" onClick={() => void restoreHandoff(h, qc)} data-testid="restore-handoff">Show in the Import list again</button>}
                          {' '}<button className="btn sm" onClick={() => setHandoff(h)} disabled={closed} data-testid="import-handoff">Check in…</button>
                        </td>
                      </tr>
                    );
                  })}</tbody></table></TableScroll>
                )}
                <label className="toggle-row" style={{ marginTop: 8 }}><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="import-show-all" />
                  <span>Show missing and cleaned up{!showAll && hiddenCount ? <span className="faint"> · {hiddenCount} hidden</span> : null} <span className="faint">(and those removed from the Import list)</span></span></label>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
